import { useEffect, useRef, useState } from "preact/hooks";
import type { CoreData, CountryId, SexPref } from "../data/types";
import { onPhone, settings, toast } from "../state/app";
import { COUNTRIES, COUNTRY_INFO, isAvailable } from "../data/countries";
import { joinRoom, peekRoom, type RoomPeek } from "../state/partner";
import { clearPendingJoin, goTab, peekPendingJoin, setPendingJoin } from "../state/router";
import { fmt } from "../data/format";
import { DATA_COUNTS } from "../data/version";
import { IconChevron, IconHeart, IconStar, IconX } from "./icons";
import { count } from "../state/telemetry";
import { onRadioKey, rovingTab } from "./radio";

const INTRO: Record<CountryId, (n: string) => string> = {
  uk: (n) => `Swipe through ${n} names from the UK's official birth records, each with real figures. Pair your phones and Nameling shows you the names you both like.`,
  us: (n) => `Swipe through ${n} names from the US Social Security records, each with real figures. Pair your phones and Nameling shows you the names you both like.`,
  other: (n) => `Swipe through ${n} names from the UK and US birth records, each with real figures. Pair your phones and Nameling shows you the names you both like.`,
};

const SEXES: [SexPref, string, string][] = [
  ["F", "A girl", "g-girl"],
  ["M", "A boy", "g-boy"],
  ["X", "Not sure yet", "g-unisex"],
];

/** what an invited partner is about to see: "Girls' names from the UK's records" */
function choosing(country: CountryId, sex: SexPref): string {
  const who = sex === "F" ? "Girls' names" : sex === "M" ? "Boys' names" : "Girls' and boys' names";
  return country === "other" ? `${who} from the UK's and US's records` : `${who} from the ${COUNTRY_INFO[country].short}'s records`;
}

/** The welcome steps. They don't wait for the names: those load while the steps are read. */
export function Onboarding({ core }: { core: CoreData | null }) {
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState<CountryId>(settings.get().country);
  const [sex, setSex] = useState<SexPref>(settings.get().sex);
  // the country step only appears when there's a choice to make; names are asked for where
  // they're used (pairing, trying a full name), so the cards come sooner
  const steps = COUNTRIES.length > 1 ? ["country", "sex"] : ["sex"];
  const at = (name: string) => (steps.includes(name) ? steps.indexOf(name) + 1 : -1);
  const [myName, setMyName] = useState(settings.get().myName);

  const [inviter, setInviter] = useState<string | null>(null);
  const [invite, setInvite] = useState<string | null>(peekPendingJoin());
  // an invite is checked before anyone types their name into it
  const [inviteState, setInviteState] = useState<"checking" | "ready" | "missing" | "full" | "offline">(invite ? "checking" : "ready");
  // an invited partner may look at the choices first; the steps then lead back to joining
  const [changing, setChanging] = useState(false);
  // a pair code typed in here, for a partner who was told the code rather than sent the link
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeText, setCodeText] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [nameError, setNameError] = useState(false);
  // what the inviter is choosing becomes the starting point, unless it's already been changed here
  const chosen = useRef({ country: false, sex: false });

  // each step starts at its heading (or the code field), so focus is never lost between steps
  const root = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const codeField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    (codeOpen ? codeField.current : title.current)?.focus({ preventScroll: true });
  }, [step, codeOpen]);
  // Tab stays inside the welcome screens
  const trap = (ev: KeyboardEvent) => {
    if (ev.key !== "Tab" || !root.current) return;
    const items = [...root.current.querySelectorAll<HTMLElement>("button:not([disabled]),input,a[href]")].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const at = document.activeElement;
    if (ev.shiftKey && (at === first || !root.current.contains(at) || at === title.current)) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && at === last) {
      ev.preventDefault();
      first.focus();
    }
  };

  const adopt = (r: RoomPeek) => {
    if (r.names[0]) setInviter(r.names[0]);
    if (r.country && isAvailable(r.country) && !chosen.current.country) setCountry(r.country);
    if (r.sex && !chosen.current.sex) setSex(r.sex);
  };

  useEffect(() => {
    if (!invite) return;
    let live = true;
    peekRoom(invite).then((r) => {
      if (!live) return;
      if (!r) return setInviteState(navigator.onLine ? "missing" : "offline");
      adopt(r);
      setInviteState(r.full ? "full" : "ready");
    });
    return () => {
      live = false;
    };
  }, [invite]);

  const checkCode = async () => {
    const clean = codeText.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length !== 8 && clean.length !== 6) return setCodeError("A pair code has eight letters and numbers, like ABCD 2345.");
    setChecking(true);
    const r = await peekRoom(clean);
    setChecking(false);
    if (!r) return setCodeError(navigator.onLine ? "That code doesn't exist. Check it and try again." : "You seem to be offline. Pairing needs a connection.");
    if (r.full) return setCodeError("That pairing already has two people. Ask your partner for a new code.");
    setCodeError(null);
    setCodeOpen(false);
    setPendingJoin(clean);
    setInvite(clean);
  };

  /** an invite that can't be used: start without it */
  const goSolo = () => {
    clearPendingJoin();
    setInvite(null);
    setInviter(null);
    setInviteState("ready");
    setStep(0);
  };

  const finish = () => {
    settings.set({ country, sex, myName: myName.trim() || settings.get().myName, onboarded: true });
    count("onboarded");
  };

  /** Invited: finish and join in one go, then straight to the cards. */
  const finishAndJoin = (code: string) => {
    finish();
    goTab("swipe");
    joinRoom(code).then(
      () => {
        clearPendingJoin();
        toast(`You're paired with ${inviter ?? "your partner"}. Swipe away: matches appear as you agree.`, undefined, 5000);
      },
      // the Together screen shows what went wrong, with the code ready to try again
      () => goTab("together"),
    );
  };

  const who = inviter ?? "your partner";
  return (
    <div class="onboard" role="dialog" aria-modal="true" aria-label={invite ? "Join your partner on Nameling" : "Welcome to Nameling"} ref={root} onKeyDown={trap}>
      <div class="onboard-inner" key={`${step}-${invite ? "join" : "welcome"}`}>
        {step === 0 && invite && (
          <>
            <div class="ob-logo" aria-hidden="true">
              <span class="ob-card c1">Isla</span>
              <span class="ob-card c2">Arthur</span>
              <span class="ob-card c3">Margot</span>
            </div>
            <h1 class="ob-title" tabIndex={-1} ref={title}>
              {inviter ? `Choose a name with ${inviter}` : "Choose a name together"}
            </h1>
            <p class="ob-text">You each swipe on your own {onPhone() ? "phone" : "device"}. When you both like a name, Nameling shows you.</p>
            {inviteState === "missing" || inviteState === "full" || inviteState === "offline" ? (
              <div class="ob-form">
                <p class="error-text" role="alert">
                  {inviteState === "full"
                    ? `That pairing already has two people. Ask ${who} for a new invite.`
                    : inviteState === "offline"
                      ? "You seem to be offline. Joining needs a connection: try again when you're back online."
                      : `That invite has ended or expired. Ask ${who} for a new one.`}
                </p>
                <button class="btn btn-primary btn-wide btn-big" onClick={goSolo}>
                  Start on your own
                </button>
              </div>
            ) : (
              <form
                class="ob-form"
                noValidate
                onSubmit={(ev) => {
                  ev.preventDefault();
                  if (!myName.trim()) return setNameError(true);
                  finishAndJoin(invite);
                }}
              >
                <label class="field">
                  <span>Your first name</span>
                  <input
                    type="text"
                    value={myName}
                    maxLength={40}
                    autocomplete="given-name"
                    placeholder={`So ${inviter ?? "they"} know it's you`}
                    aria-invalid={nameError}
                    aria-describedby={nameError ? "ob-name-error" : undefined}
                    onInput={(ev) => {
                      setMyName((ev.target as HTMLInputElement).value);
                      setNameError(false);
                    }}
                  />
                </label>
                {nameError && (
                  <p class="error-text" id="ob-name-error" role="alert">
                    Add your first name so {who} knows it's you.
                  </p>
                )}
                <button class="btn btn-primary btn-wide btn-big" type="submit" disabled={inviteState === "checking"}>
                  {inviteState === "checking" ? "Checking the invite…" : `Join ${inviter ?? "your partner"}`}
                </button>
                <p class="ob-text small ob-choosing">
                  {choosing(country, sex)}.{" "}
                  <button
                    type="button"
                    class="link-btn"
                    onClick={() => {
                      setChanging(true);
                      setStep(1);
                    }}
                  >
                    Change
                  </button>
                </p>
                <p class="ob-text small ob-promise">No account needed. {inviter ?? "Your partner"} sees your first name and the names you like.</p>
              </form>
            )}
          </>
        )}
        {step === 0 && !invite && (
          <>
            <div class="ob-logo" aria-hidden="true">
              <span class="ob-card c1">Isla</span>
              <span class="ob-card c2">Arthur</span>
              <span class="ob-card c3">Margot</span>
            </div>
            <h1 class="ob-title" tabIndex={-1} ref={title}>
              Find a name you both love
            </h1>
            <p class="ob-text">{INTRO[country](fmt(DATA_COUNTS[country] ?? core?.entries.length ?? 0))}</p>
            <div class="ob-legend" role="group" aria-label="How to swipe">
              <span>
                <IconX size={18} /> Left: nope
              </span>
              <span>
                <IconHeart size={18} filled /> Right: like
              </span>
              <span>
                <IconStar size={18} filled /> Up: love, for favourites
              </span>
            </div>
            {!codeOpen ? (
              <>
                <button class="btn btn-primary btn-wide btn-big" onClick={() => setStep(1)}>
                  Get started <IconChevron dir="right" size={20} />
                </button>
                <p class="ob-text small ob-promise">No account needed. Your swipes stay on {onPhone() ? "your phone" : "this computer"}.</p>
                <button class="btn btn-ghost ob-code-link" onClick={() => setCodeOpen(true)}>
                  I've got a pair code
                </button>
                {/* the website's own pages, for browsing first (and a way in for search engines) */}
                {!__NATIVE__ && !__PREVIEW__ && (
                  <p class="ob-text small ob-browse">
                    Or browse first: <a href="/names">names A to Z</a> ·{" "}
                    <a href={`/names/trends/${country === "us" ? "us" : "uk"}`}>name trends</a>
                  </p>
                )}
              </>
            ) : (
              <form
                class="ob-form ob-code"
                noValidate
                onSubmit={(ev) => {
                  ev.preventDefault();
                  checkCode();
                }}
              >
                <label class="field">
                  <span>Your partner's pair code</span>
                  <input
                    ref={codeField}
                    class="code-input"
                    type="text"
                    inputMode="text"
                    autocapitalize="characters"
                    autocomplete="one-time-code"
                    maxLength={9}
                    value={codeText}
                    placeholder="ABCD 2345"
                    aria-invalid={!!codeError}
                    aria-describedby={codeError ? "ob-code-error" : undefined}
                    onInput={(ev) => setCodeText((ev.target as HTMLInputElement).value.toUpperCase())}
                  />
                </label>
                {codeError && (
                  <p class="error-text" id="ob-code-error" role="alert">
                    {codeError}
                  </p>
                )}
                <button class="btn btn-primary btn-wide btn-big" type="submit" disabled={checking}>
                  {checking ? "Checking…" : "Continue"}
                </button>
                <button
                  type="button"
                  class="btn btn-ghost btn-wide"
                  onClick={() => {
                    setCodeOpen(false);
                    setCodeError(null);
                  }}
                >
                  Back
                </button>
              </form>
            )}
          </>
        )}
        {step === at("country") && (
          <>
            <p class="ob-step">
              {at("country")} of {steps.length}
            </p>
            <h1 class="ob-title" tabIndex={-1} ref={title}>Where will the baby grow up?</h1>
            <div class="ob-choices" role="radiogroup" aria-label="Country">
              {COUNTRIES.map((c, i) => (
                <button
                  key={c}
                  role="radio"
                  aria-checked={country === c}
                  tabIndex={rovingTab(i, country === c, COUNTRIES.includes(country))}
                  class={`ob-choice ob-country ${country === c ? "on" : ""}`}
                  onClick={() => {
                    chosen.current.country = true;
                    setCountry(c);
                  }}
                  onKeyDown={(ev) =>
                    onRadioKey(ev, i, COUNTRIES.length, (j) => {
                      chosen.current.country = true;
                      setCountry(COUNTRIES[j]);
                    })
                  }
                >
                  <span class="ob-choice-name">{COUNTRY_INFO[c].name}</span>
                  <span class="ob-choice-sub">{COUNTRY_INFO[c].blurb}</span>
                </button>
              ))}
            </div>
            <p class="ob-text small">
              {country === "other"
                ? "Nameling has the UK's and the US's records so far, so it adds the two together. The US has about six times as many births, so the figures lean American. You can change this in Settings."
                : invite && inviter
                  ? `${inviter} is looking at names from here. Popularity figures come from this country's records.`
                  : "Popularity figures come from this country's records. You can change it in Settings."}
            </p>
            <button class="btn btn-primary btn-wide btn-big" onClick={() => setStep(step + 1)}>
              Next <IconChevron dir="right" size={20} />
            </button>
          </>
        )}
        {step === at("sex") && (
          <>
            <p class="ob-step">
              {at("sex")} of {steps.length}
            </p>
            <h1 class="ob-title" tabIndex={-1} ref={title}>Who are we naming?</h1>
            <div class="ob-choices" role="radiogroup" aria-label="Names for">
              {SEXES.map(([v, label, cls], i) => (
                <button
                  key={v}
                  role="radio"
                  aria-checked={sex === v}
                  tabIndex={rovingTab(i, sex === v, true)}
                  class={`ob-choice ${cls} ${sex === v ? "on" : ""}`}
                  onClick={() => {
                    chosen.current.sex = true;
                    setSex(v);
                  }}
                  onKeyDown={(ev) =>
                    onRadioKey(ev, i, SEXES.length, (j) => {
                      chosen.current.sex = true;
                      setSex(SEXES[j][0]);
                    })
                  }
                >
                  <span class="gdot" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
            <p class="ob-text small">You can switch at any time in the filters.</p>
            {changing ? (
              <button
                class="btn btn-primary btn-wide btn-big"
                onClick={() => {
                  setChanging(false);
                  setStep(0);
                }}
              >
                Done
              </button>
            ) : (
              <button class="btn btn-primary btn-wide btn-big" onClick={finish}>
                Start swiping
              </button>
            )}
          </>
        )}
      </div>
      {step > 0 && (
        <button class="ob-back" onClick={() => setStep(step - 1)} aria-label="Back">
          <IconChevron dir="left" size={22} />
        </button>
      )}
    </div>
  );
}
